import React, { FC, useCallback, useState } from "react";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import Loader from "@components/Loader/Loader";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useDeleteCharacterMutation,
  useGetCharactersQuery,
} from "@store/characterApi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import CharactersForm from "@components/CharactersForm/CharactersForm";
import { useForm } from "react-hook-form";
import TextIconButton from "@components/TextIconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDebounceValue } from "@hooks/useDebounceValue";
import { Character } from "@models/character";
import { setMessage } from "@store/snackbarSlice";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import EmptyState from "@components/EmptyState/EmptyState";

const Faces: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [checkedCharacters, setCheckedCharacters] = useState<Set<Character>>(
    new Set(),
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const searchForm = useForm<{ search: string }>();
  const searchStr = useDebounceValue(searchForm.watch("search"), 500);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [deleteCharacter, { isLoading: isDeleting }] =
    useDeleteCharacterMutation();
  const { data: charactersData, isLoading: isLoadingCharacters } =
    useGetCharactersQuery(
      {
        patientId: selectedPatientId!,
        search: searchStr,
        includeFaces: true,
      },
      { skip: !selectedPatientId },
    );

  const deleteSelectedCharacters = useCallback(async () => {
    if (!checkedCharacters?.size) {
      return;
    }

    for (const character of Array.from(checkedCharacters)) {
      const { error } = await deleteCharacter(character.id);

      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    }
    setCheckedCharacters(new Set());
    setConfirmDialogOpen(false);
  }, [checkedCharacters, deleteCharacter, dispatch, t]);

  const checkCharacter = useCallback((character: Character) => {
    setCheckedCharacters((characters: Set<Character>) => {
      const newSet = new Set(characters);
      if (newSet.has(character)) {
        newSet.delete(character);
      } else {
        newSet.add(character);
      }
      return newSet;
    });
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <HomePageTitle
        title=""
        search={{
          placeholder: t("faces.searchInput.placeholder"),
          form: searchForm,
        }}
      />
      {isLoadingCharacters ? (
        <Loader />
      ) : (
        <>
          <Box marginBottom="32px">
            <TextIconButton
              text={t("faces.deleteButton")}
              disabled={!checkedCharacters.size}
              variant="outlined"
              color="error"
              icon={DeleteOutlineOutlinedIcon}
              onClick={() => setConfirmDialogOpen(true)}
            />
          </Box>
          {charactersData?.data?.length ? (
            <CharactersForm
              characters={charactersData.data}
              patientId={selectedPatientId!}
              onCheck={checkCharacter}
              checkedCharacters={checkedCharacters}
              enableViewDrawer={true}
            />
          ) : (
            <EmptyState
              image={`${process.env.PUBLIC_URL}/images/art-faces.jpeg`}
              label={t("faces.noData")}
            />
          )}

          <ConfirmDialog
            processing={isDeleting}
            open={confirmDialogOpen}
            onCancel={() => setConfirmDialogOpen(false)}
            onConfirm={deleteSelectedCharacters}
          />
        </>
      )}
    </Box>
  );
};

export default Faces;
